import { useState, useEffect, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Users, Store, TrendingUp, Trash2, Ban, Shield, Phone, Mail,
  ChevronRight, Edit2, Save, Calendar, CreditCard,
  MessageCircle, MapPin, Clock, Search, UserCheck, AlertTriangle,
  ArrowLeft, Check, XCircle, Loader2, Headset, Send, Lock, Eye, EyeOff, LogIn
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getSavedEmails, addSavedEmail, removeSavedEmail } from "@/components/AuthModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_EMAILS = ["admin@bookeasy.com", "titolare@bookeasy.it"];

export default function AdminDashboard() {
  const { user, setUser, loading: authLoading, checkAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Admin login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [adminResetResult, setAdminResetResult] = useState(null);
  const [adminSavedEmails, setAdminSavedEmails] = useState(getSavedEmails());

  const isAdmin = user && (ADMIN_EMAILS.includes(user.email) || user.role === "admin");

  // Detail panels
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [bizBookings, setBizBookings] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editingBiz, setEditingBiz] = useState(null);
  const [searchClients, setSearchClients] = useState("");
  const [searchBiz, setSearchBiz] = useState("");
  const [saving, setSaving] = useState(false);

  // Support chat state
  const [supportConvos, setSupportConvos] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [convoMessages, setConvoMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const chatEndRef = useRef(null);
  const pollConvoRef = useRef(null);

  // Confirm delete state
  const [confirmDelete, setConfirmDelete] = useState(null); // { type: "user"|"biz", id, name }

  // All bookings state
  const [allBookings, setAllBookings] = useState([]);
  const [searchBookings, setSearchBookings] = useState("");

  useEffect(() => {
    if (isAdmin) { fetchData(); fetchSupportConvos(); }
    else { setLoading(false); }
  }, [isAdmin]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPassword }, { withCredentials: true });
      if (res.data?.session_token) localStorage.setItem('bookeasy_session', res.data.session_token);
      // Save email for quick access
      addSavedEmail(loginEmail);
      // Set user directly - don't call checkAuth (avoids race condition with cookie)
      const userData = { ...res.data };
      delete userData.session_token;
      setUser(userData);
      toast.success("Accesso admin effettuato!");
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Email o password non validi");
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, bizRes, bookRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/businesses`, { withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setAllUsers(usersRes.data);
      setAllBusinesses(bizRes.data);
      setAllBookings(bookRes.data || []);
    } catch (err) {
      toast.error("Errore caricamento dati admin");
    } finally {
      setLoading(false);
    }
  };

  // ===== SUPPORT CHAT FUNCTIONS =====
  const fetchSupportConvos = async () => {
    try {
      const [convRes, unreadRes] = await Promise.all([
        axios.get(`${API}/support/conversations`, { withCredentials: true }),
        axios.get(`${API}/support/admin-unread`, { withCredentials: true }),
      ]);
      setSupportConvos(convRes.data || []);
      setSupportUnread(unreadRes.data.unread || 0);
    } catch {}
  };

  const openConvo = async (userId) => {
    setActiveConvo(userId);
    try {
      const res = await axios.get(`${API}/support/conversations/${userId}`, { withCredentials: true });
      setConvoMessages(res.data || []);
      fetchSupportConvos();
    } catch { setConvoMessages([]); }
    // Start polling
    if (pollConvoRef.current) clearInterval(pollConvoRef.current);
    pollConvoRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/support/conversations/${userId}`, { withCredentials: true });
        setConvoMessages(res.data || []);
      } catch {}
    }, 5000);
  };

  const closeConvo = () => {
    setActiveConvo(null);
    setConvoMessages([]);
    setReplyText("");
    if (pollConvoRef.current) clearInterval(pollConvoRef.current);
    fetchSupportConvos();
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeConvo) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/support/reply/${activeConvo}`, { content: replyText.trim() }, { withCredentials: true });
      setReplyText("");
      const res = await axios.get(`${API}/support/conversations/${activeConvo}`, { withCredentials: true });
      setConvoMessages(res.data || []);
    } catch { toast.error("Errore invio risposta"); }
    finally { setSendingReply(false); }
  };

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [convoMessages]);

  useEffect(() => {
    return () => { if (pollConvoRef.current) clearInterval(pollConvoRef.current); };
  }, []);

  const clients = allUsers.filter(u => u.role === "customer");
  const bizOwners = allUsers.filter(u => u.role === "business");
  const noRole = allUsers.filter(u => !u.role);

  const filteredClients = clients.filter(u =>
    u.name?.toLowerCase().includes(searchClients.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchClients.toLowerCase())
  );

  const filteredBiz = allBusinesses.filter(b =>
    b.name?.toLowerCase().includes(searchBiz.toLowerCase()) ||
    b.category?.toLowerCase().includes(searchBiz.toLowerCase())
  );

  // ===== USER ACTIONS =====
  const openUser = async (user) => {
    setSelectedUser(user);
    setEditingUser({ name: user.name || "", email: user.email || "", phone: user.phone || "", role: user.role || "" });
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}/bookings`, { withCredentials: true });
      setUserBookings(res.data || []);
    } catch { setUserBookings([]); }
  };

  const saveUser = async () => {
    if (!selectedUser || !editingUser) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/users/${selectedUser.user_id}/edit`, editingUser, { withCredentials: true });
      setAllUsers(prev => prev.map(u => u.user_id === selectedUser.user_id ? { ...u, ...res.data } : u));
      setSelectedUser(prev => ({ ...prev, ...res.data }));
      toast.success("Profilo utente aggiornato!");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const toggleBan = async (userId, currentBanned) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/ban`, {}, { withCredentials: true });
      setAllUsers(prev => prev.map(u => u.user_id === userId ? { ...u, banned: !currentBanned } : u));
      if (selectedUser?.user_id === userId) setSelectedUser(prev => ({ ...prev, banned: !currentBanned }));
      toast.success(currentBanned ? "Utente sbloccato" : "Utente bloccato!");
    } catch { toast.error("Errore"); }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      setAllUsers(prev => prev.filter(u => u.user_id !== userId));
      setSelectedUser(null);
      setConfirmDelete(null);
      toast.success("Utente eliminato");
    } catch { toast.error("Errore eliminazione"); }
  };

  // ===== BUSINESS ACTIONS =====
  const openBiz = async (biz) => {
    setSelectedBiz(biz);
    setEditingBiz({ ...biz });
    try {
      const res = await axios.get(`${API}/admin/businesses/${biz.business_id}/bookings`, { withCredentials: true });
      setBizBookings(res.data || []);
    } catch { setBizBookings([]); }
  };

  const saveBiz = async () => {
    if (!selectedBiz || !editingBiz) return;
    setSaving(true);
    try {
      const updates = {};
      ["name", "description", "address", "phone", "whatsapp", "deposit_amount", "satispay_phone", "category"].forEach(k => {
        if (editingBiz[k] !== selectedBiz[k]) updates[k] = editingBiz[k];
      });
      if (Object.keys(updates).length === 0) { toast.info("Nessuna modifica"); setSaving(false); return; }
      await axios.put(`${API}/businesses/${selectedBiz.business_id}`, updates, { withCredentials: true });
      setAllBusinesses(prev => prev.map(b => b.business_id === selectedBiz.business_id ? { ...b, ...updates } : b));
      setSelectedBiz(prev => ({ ...prev, ...updates }));
      toast.success("Attivita aggiornata!");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const deleteBiz = async (bizId) => {
    try {
      await axios.delete(`${API}/admin/businesses/${bizId}`, { withCredentials: true });
      setAllBusinesses(prev => prev.filter(b => b.business_id !== bizId));
      setSelectedBiz(null);
      setConfirmDelete(null);
      toast.success("Attivita eliminata");
    } catch { toast.error("Errore eliminazione"); }
  };

  // Admin change booking status
  const adminChangeBookingStatus = async (bookingId, newStatus, forUser) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status: newStatus }, { withCredentials: true });
      if (forUser === "user") {
        setUserBookings(prev => prev.map(b => b.booking_id === bookingId ? { ...b, status: newStatus } : b));
      } else {
        setBizBookings(prev => prev.map(b => b.booking_id === bookingId ? { ...b, status: newStatus } : b));
      }
      toast.success(`Prenotazione aggiornata: ${newStatus}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore aggiornamento"); }
  };

  // === ADMIN LOGIN SCREEN ===
  if (authLoading) return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-[#FF4A00] border-t-transparent rounded-full" />
    </div>
  );

  if (!isAdmin) return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center px-4">
      {/* Minimal logo header - no Navbar to avoid Google/Apple auth distractions */}
      <button
        onClick={() => window.location.href = '/'}
        className="flex items-center gap-2 font-heading text-xl tracking-tight text-[#09090B] mb-8 hover:text-[#FF4A00] transition-colors"
        data-testid="admin-logo-home"
      >
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#E83E00] flex items-center justify-center shadow-sm border-2 border-[#09090B]">
          <span className="text-white font-black text-sm">B</span>
        </div>
        Book<span className="text-[#FF4A00]">Easy</span>
      </button>

      <div className="bg-white border-2 border-[#09090B] rounded-2xl shadow-[4px_4px_0_#09090B] p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#09090B] flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-[#FFE600]" />
            </div>
            <h1 className="font-heading text-2xl font-black text-[#09090B]" data-testid="admin-login-title">Accesso Amministratore</h1>
            <p className="text-sm text-[#6A564A] mt-1">Inserisci le credenziali titolare</p>
          </div>
          <form onSubmit={handleAdminLogin} className="space-y-4">
            {/* Saved emails - quick select */}
            {adminSavedEmails.length > 0 && (
              <div className="space-y-1.5" data-testid="admin-saved-emails">
                <p className="text-xs font-medium text-[#6A564A]">Account salvati:</p>
                {adminSavedEmails.map(email => (
                  <div
                    key={email}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                      loginEmail === email
                        ? "border-[#FF4A00] bg-[#FF4A00]/5"
                        : "border-[#09090B]/10 hover:border-[#FF4A00]/40"
                    }`}
                    onClick={() => setLoginEmail(email)}
                    data-testid={`admin-saved-${email}`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${loginEmail === email ? "bg-[#FF4A00]" : "bg-[#6A564A]"}`}>
                      {email[0].toUpperCase()}
                    </div>
                    <span className="text-sm text-[#09090B] font-medium flex-1 truncate">{email}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeSavedEmail(email); setAdminSavedEmails(getSavedEmails()); }}
                      className="p-1 rounded-full hover:bg-red-50 text-[#6A564A] hover:text-red-500 transition-colors"
                      data-testid={`admin-remove-${email}`}
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6A564A]" />
              <Input
                type="email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                placeholder="Email titolare"
                className="pl-11 rounded-xl border-2 border-[#09090B]/20 h-12"
                required
                data-testid="admin-login-email"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6A564A]" />
              <Input
                type={showPwd ? "text" : "password"}
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                placeholder="Password"
                className="pl-11 pr-11 rounded-xl border-2 border-[#09090B]/20 h-12"
                required
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6A564A] hover:text-[#09090B]"
                data-testid="admin-toggle-pwd"
              >
                {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm text-red-600 font-medium" data-testid="admin-login-error">
                {loginError}
              </div>
            )}
            <Button
              type="submit"
              disabled={loginLoading}
              className="w-full rounded-xl bg-[#FF4A00] text-white hover:bg-[#E04400] border-0 font-bold h-12 text-base btn-float"
              data-testid="admin-login-submit"
            >
              {loginLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <LogIn className="h-5 w-5 mr-2" />}
              Accedi come Titolare
            </Button>
            <button
              type="button"
              onClick={async () => {
                if (!loginEmail) { toast.error("Inserisci la tua email prima"); return; }
                try {
                  const res = await axios.post(`${API}/auth/reset-password`, { email: loginEmail });
                  setAdminResetResult(res.data);
                  setLoginError("");
                } catch (err) {
                  setLoginError(err.response?.data?.detail || "Errore");
                }
              }}
              className="w-full text-center text-sm text-[#FF4A00] hover:underline font-medium py-1"
              data-testid="admin-forgot-pwd"
            >
              Password dimenticata?
            </button>
            {adminResetResult && (
              <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4 space-y-2" data-testid="admin-reset-result">
                <p className="text-sm font-bold text-emerald-700">Password resettata!</p>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-emerald-200">
                  <span className="text-base font-mono font-bold text-[#09090B] flex-1">{adminResetResult.new_password}</span>
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard.writeText(adminResetResult.new_password); toast.success("Copiata!"); }}
                    className="text-xs bg-emerald-500 text-white px-3 py-1 rounded-lg font-bold hover:bg-emerald-600"
                  >
                    Copia
                  </button>
                </div>
              </div>
            )}
          </form>
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin h-8 w-8 border-4 border-[#FF4A00] border-t-transparent rounded-full" />
      </div>
    </div>
  );

  const statusColors = {
    confirmed: "bg-emerald-100 text-emerald-700 border-emerald-300",
    pending: "bg-amber-100 text-amber-700 border-amber-300",
    cancelled: "bg-red-100 text-red-700 border-red-300",
    completed: "bg-blue-100 text-blue-700 border-blue-300"
  };

  const statusLabel = {
    confirmed: "Confermata",
    pending: "In attesa",
    cancelled: "Cancellata",
    completed: "Completata"
  };

  // Reusable booking card for both user and business views
  const BookingCard = ({ bk, context }) => (
    <div key={bk.booking_id} className="bg-white border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`booking-${bk.booking_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 text-[#FF4A00]" />
          <span className="font-bold text-sm text-[#09090B]">{bk.date} — {bk.time_slot}</span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusColors[bk.status] || "bg-gray-100 text-gray-600"}`}>
          {statusLabel[bk.status] || bk.status}
        </span>
      </div>
      <div className="text-xs text-[#6A564A] space-y-0.5">
        {context === "user" && <p>Attivita: <strong>{bk.business_name || "N/A"}</strong></p>}
        {context === "biz" && <p>Cliente: <strong>{bk.customer_name || "N/A"}</strong> — {bk.customer_email || ""}</p>}
        {bk.amount > 0 && <p>Importo: <strong>{bk.amount}€</strong></p>}
        {bk.customer_notes && <p>Note: <em>{bk.customer_notes}</em></p>}
      </div>
      {/* Admin status controls */}
      <div className="flex gap-1.5 pt-1">
        {["confirmed", "cancelled", "completed"].filter(s => s !== bk.status).map(s => (
          <button
            key={s}
            onClick={() => adminChangeBookingStatus(bk.booking_id, s, context === "user" ? "user" : "biz")}
            className={`text-[10px] font-bold px-2 py-1 rounded-lg border transition-all hover:scale-105 ${statusColors[s]}`}
            data-testid={`booking-${bk.booking_id}-${s}`}
          >
            {s === "confirmed" ? "Conferma" : s === "cancelled" ? "Cancella" : "Completa"}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 pt-20 pb-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#09090B] flex items-center justify-center">
            <Shield className="h-5 w-5 text-[#FFE600]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-black text-[#09090B]" data-testid="admin-title">Pannello Amministratore</h1>
            <p className="text-xs text-[#6A564A]" data-testid="admin-logged-email">Accesso: <span className="font-bold text-[#FF4A00]">{user?.email}</span> — {allUsers.length} utenti — {allBusinesses.length} attivita</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="admin-tabs">
          {[
            { id: "overview", label: "Panoramica", icon: TrendingUp },
            { id: "clients", label: `Clienti (${clients.length})`, icon: Users },
            { id: "businesses", label: `Aziende (${allBusinesses.length})`, icon: Store },
            { id: "bookings", label: `Prenotazioni (${allBookings.length})`, icon: Calendar },
            { id: "support", label: `Assistenza${supportUnread > 0 ? ` (${supportUnread})` : ""}`, icon: Headset },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm border-2 transition-all whitespace-nowrap btn-float ${
                activeTab === tab.id
                  ? "bg-[#09090B] text-white border-[#09090B]"
                  : "bg-white text-[#09090B] border-[#09090B]/20 hover:border-[#09090B]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ===== OVERVIEW ===== */}
        {activeTab === "overview" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Clienti", value: clients.length, color: "bg-emerald-500", icon: Users, tab: "clients" },
                { label: "Aziende", value: allBusinesses.length, color: "bg-blue-500", icon: Store, tab: "businesses" },
                { label: "Prenotazioni", value: stats.total_bookings, color: "bg-amber-500", icon: Calendar, tab: "bookings" },
                { label: "Commissioni", value: `${stats.platform_fees?.toFixed(2) || 0}€`, color: "bg-[#FF4A00]", icon: CreditCard, tab: null },
              ].map(s => (
                <div
                  key={s.label}
                  onClick={() => s.tab && setActiveTab(s.tab)}
                  className={`bg-white border-2 border-[#09090B] rounded-2xl p-5 shadow-[3px_3px_0_#09090B] card-tap transition-all ${s.tab ? "cursor-pointer hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none" : ""}`}
                  data-testid={`stat-${s.label}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center`}>
                      <s.icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-xs uppercase tracking-widest text-[#6A564A] font-bold">{s.label}</span>
                  </div>
                  <p className="font-heading text-3xl font-black text-[#09090B]">{s.value}</p>
                  {s.tab && <p className="text-[10px] text-[#FF4A00] font-bold mt-1">Clicca per vedere →</p>}
                </div>
              ))}
            </div>

            {noRole.length > 0 && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-bold text-amber-700 text-sm">{noRole.length} utenti senza ruolo</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {noRole.slice(0, 10).map(u => (
                    <span key={u.user_id} className="text-xs bg-white border border-amber-200 rounded-lg px-2 py-1 text-amber-700">
                      {u.email}
                    </span>
                  ))}
                  {noRole.length > 10 && <span className="text-xs text-amber-500">+{noRole.length - 10} altri</span>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== CLIENTS TAB ===== */}
        {activeTab === "clients" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-[#6A564A]" />
              <Input
                placeholder="Cerca cliente per nome o email..."
                value={searchClients}
                onChange={e => setSearchClients(e.target.value)}
                className="pl-10 rounded-xl border-2 border-[#09090B]/20 h-11"
                data-testid="search-clients"
              />
            </div>
            {filteredClients.length === 0 ? (
              <div className="bg-white border-2 border-[#09090B] rounded-2xl p-10 text-center shadow-[3px_3px_0_#09090B]">
                <Users className="h-8 w-8 text-[#6A564A] mx-auto mb-2" />
                <p className="text-[#6A564A]">Nessun cliente trovato</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredClients.map(u => (
                  <div
                    key={u.user_id}
                    className={`bg-white border-2 rounded-2xl p-4 cursor-pointer transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none card-tap ${
                      u.banned ? "border-red-400 shadow-[3px_3px_0_#EF4444]" : "border-[#09090B] shadow-[3px_3px_0_#09090B]"
                    }`}
                    onClick={() => openUser(u)}
                    data-testid={`client-${u.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${u.banned ? "bg-red-500" : "bg-emerald-500"}`}>
                          {u.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[#09090B]">{u.name || "Senza nome"}</span>
                            {u.banned && <Badge variant="destructive" className="text-[10px]">BLOCCATO</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-[#6A564A]">
                            <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{u.email}</span>
                            {u.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{u.phone}</span>}
                          </div>
                          <span className="text-[10px] text-[#9CA3AF]">
                            Registrato: {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "N/A"} — via {u.auth_provider || "email"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={e => { e.stopPropagation(); toggleBan(u.user_id, u.banned); }}
                          className={`p-2 rounded-xl border-2 transition-all ${u.banned ? "border-emerald-500 bg-emerald-50 text-emerald-600 hover:bg-emerald-100" : "border-red-400 bg-red-50 text-red-500 hover:bg-red-100"}`}
                          title={u.banned ? "Sblocca" : "Blocca"}
                          data-testid={`ban-${u.user_id}`}
                        >
                          {u.banned ? <UserCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); setConfirmDelete({ type: "user", id: u.user_id, name: u.name || u.email }); }}
                          className="p-2 rounded-xl border-2 border-red-400 bg-red-50 text-red-500 hover:bg-red-100 transition-all"
                          title="Elimina"
                          data-testid={`delete-${u.user_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <ChevronRight className="h-4 w-4 text-[#6A564A]" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== BUSINESSES TAB ===== */}
        {activeTab === "businesses" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-[#6A564A]" />
              <Input
                placeholder="Cerca attivita per nome o categoria..."
                value={searchBiz}
                onChange={e => setSearchBiz(e.target.value)}
                className="pl-10 rounded-xl border-2 border-[#09090B]/20 h-11"
                data-testid="search-businesses"
              />
            </div>
            {filteredBiz.length === 0 ? (
              <div className="bg-white border-2 border-[#09090B] rounded-2xl p-10 text-center shadow-[3px_3px_0_#09090B]">
                <Store className="h-8 w-8 text-[#6A564A] mx-auto mb-2" />
                <p className="text-[#6A564A]">Nessuna attivita trovata</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredBiz.map(b => (
                  <div
                    key={b.business_id}
                    className="bg-white border-2 border-[#09090B] rounded-2xl p-4 shadow-[3px_3px_0_#09090B] cursor-pointer transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none card-tap"
                    onClick={() => openBiz(b)}
                    data-testid={`biz-${b.business_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl border-2 border-[#09090B] bg-[#FFE600] flex items-center justify-center font-heading text-lg text-[#09090B]">
                          {b.name?.[0]?.toUpperCase() || "A"}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-[#09090B]">{b.name}</span>
                            <Badge className="bg-[#FF4A00] text-white border-0 text-[10px]">{b.category}</Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-[#6A564A] mt-0.5">
                            {b.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{b.address}</span>}
                            {b.whatsapp && <span className="flex items-center gap-1 text-[#25D366] font-bold"><MessageCircle className="h-3 w-3" />{b.whatsapp}</span>}
                          </div>
                          <div className="flex gap-2 mt-1">
                            {b.deposit_amount > 0 && <span className="text-[10px] bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Cauzione: {b.deposit_amount}€</span>}
                            {b.satispay_phone && <span className="text-[10px] bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Satispay: {b.satispay_phone}</span>}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#6A564A]" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* ===== BOOKINGS TAB ===== */}
        {activeTab === "bookings" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-[#6A564A]" />
              <Input
                placeholder="Cerca per cliente, attivita o data..."
                value={searchBookings}
                onChange={e => setSearchBookings(e.target.value)}
                className="pl-10 rounded-xl border-2 border-[#09090B]/20 h-11"
                data-testid="search-bookings"
              />
            </div>
            {(() => {
              const filtered = allBookings.filter(b =>
                b.customer_name?.toLowerCase().includes(searchBookings.toLowerCase()) ||
                b.customer_email?.toLowerCase().includes(searchBookings.toLowerCase()) ||
                b.business_name?.toLowerCase().includes(searchBookings.toLowerCase()) ||
                b.date?.includes(searchBookings)
              );
              return filtered.length === 0 ? (
                <div className="bg-white border-2 border-[#09090B] rounded-2xl p-10 text-center shadow-[3px_3px_0_#09090B]">
                  <Calendar className="h-8 w-8 text-[#6A564A] mx-auto mb-2" />
                  <p className="text-[#6A564A]">Nessuna prenotazione trovata</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filtered.map(bk => (
                    <div
                      key={bk.booking_id}
                      className="bg-white border-2 border-[#09090B] rounded-2xl p-4 shadow-[3px_3px_0_#09090B] card-tap transition-all"
                      data-testid={`all-booking-${bk.booking_id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-[#FF4A00]" />
                          <span className="font-bold text-sm text-[#09090B]">{bk.date} — {bk.time_slot}</span>
                        </div>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                          bk.status === "confirmed" ? "bg-emerald-100 text-emerald-700 border-emerald-300" :
                          bk.status === "pending" ? "bg-amber-100 text-amber-700 border-amber-300" :
                          bk.status === "cancelled" ? "bg-red-100 text-red-700 border-red-300" :
                          "bg-blue-100 text-blue-700 border-blue-300"
                        }`}>
                          {bk.status === "confirmed" ? "Confermata" : bk.status === "pending" ? "In attesa" : bk.status === "cancelled" ? "Cancellata" : bk.status === "completed" ? "Completata" : bk.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-[#6A564A]">
                        <p>Cliente: <strong className="text-[#09090B]">{bk.customer_name || "N/A"}</strong> — {bk.customer_email || ""}</p>
                        <p>Attivita: <strong className="text-[#09090B]">{bk.business_name || "N/A"}</strong></p>
                        {bk.amount > 0 && <p>Importo: <strong className="text-[#FF4A00]">{bk.amount}€</strong></p>}
                        {bk.customer_notes && <p>Note: <em>{bk.customer_notes}</em></p>}
                      </div>
                      <div className="flex gap-1.5 pt-2 mt-2 border-t border-slate-100">
                        {["confirmed", "cancelled", "completed"].filter(s => s !== bk.status).map(s => (
                          <button
                            key={s}
                            onClick={() => {
                              adminChangeBookingStatus(bk.booking_id, s, "biz");
                              setAllBookings(prev => prev.map(b => b.booking_id === bk.booking_id ? { ...b, status: s } : b));
                            }}
                            className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-all btn-float ${
                              s === "confirmed" ? "bg-emerald-100 text-emerald-700 border-emerald-300" :
                              s === "cancelled" ? "bg-red-100 text-red-700 border-red-300" :
                              "bg-blue-100 text-blue-700 border-blue-300"
                            }`}
                            data-testid={`all-booking-${bk.booking_id}-${s}`}
                          >
                            {s === "confirmed" ? "Conferma" : s === "cancelled" ? "Cancella" : "Completa"}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* ===== SUPPORT TAB ===== */}
        {activeTab === "support" && (
          <div className="space-y-4">
            {!activeConvo ? (
              /* Conversation list */
              <>
                {supportConvos.length === 0 ? (
                  <div className="bg-white border-2 border-[#09090B] rounded-2xl p-10 text-center shadow-[3px_3px_0_#09090B]">
                    <Headset className="h-8 w-8 text-[#6A564A] mx-auto mb-2" />
                    <p className="text-[#6A564A] font-bold">Nessuna richiesta di assistenza</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Quando un utente ti scrive, apparira qui</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {supportConvos.map(c => (
                      <div
                        key={c.user_id}
                        className={`bg-white border-2 rounded-2xl p-4 cursor-pointer transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none ${
                          c.unread_count > 0 ? "border-[#FF4A00] shadow-[3px_3px_0_#FF4A00]" : "border-[#09090B] shadow-[3px_3px_0_#09090B]"
                        }`}
                        onClick={() => openConvo(c.user_id)}
                        data-testid={`support-convo-${c.user_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              c.user_role === "business" ? "bg-blue-500" : "bg-emerald-500"
                            }`}>
                              {c.user_name?.[0]?.toUpperCase() || "?"}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-[#09090B]">{c.user_name || "Utente"}</span>
                                <Badge className={`text-[10px] border-0 ${c.user_role === "business" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                                  {c.user_role === "business" ? "Attivita" : "Cliente"}
                                </Badge>
                                {c.unread_count > 0 && (
                                  <Badge className="bg-[#FF4A00] text-white border-0 text-[10px]">{c.unread_count} nuov{c.unread_count === 1 ? "o" : "i"}</Badge>
                                )}
                              </div>
                              <p className="text-xs text-[#6A564A]">{c.user_email}</p>
                              <p className="text-xs text-[#9CA3AF] mt-0.5 truncate max-w-md">
                                {c.last_sender === "admin" ? "Tu: " : ""}{c.last_message}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-[#9CA3AF]">
                              {c.last_time ? new Date(c.last_time).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                            </span>
                            <ChevronRight className="h-4 w-4 text-[#6A564A]" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Active conversation */
              <div className="bg-white border-2 border-[#09090B] rounded-2xl shadow-[3px_3px_0_#09090B] overflow-hidden" data-testid="support-active-convo">
                {/* Header */}
                <div className="bg-[#09090B] text-white px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={closeConvo} className="p-1 rounded-lg hover:bg-white/10 transition-colors" data-testid="support-back-btn">
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div>
                      <p className="font-bold text-sm">
                        {supportConvos.find(c => c.user_id === activeConvo)?.user_name || "Utente"}
                      </p>
                      <p className="text-[10px] text-white/60">
                        {supportConvos.find(c => c.user_id === activeConvo)?.user_email || ""}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-white/10 text-white border-0 text-[10px]">
                    {supportConvos.find(c => c.user_id === activeConvo)?.user_role === "business" ? "Attivita" : "Cliente"}
                  </Badge>
                </div>

                {/* Messages */}
                <div className="px-4 py-3 space-y-3 min-h-[350px] max-h-[450px] overflow-y-auto bg-[#FDFBF7]">
                  {convoMessages.length === 0 && (
                    <div className="text-center text-sm text-[#6A564A] py-10">Nessun messaggio</div>
                  )}
                  {convoMessages.map(m => (
                    <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                          m.sender === "admin"
                            ? "bg-[#FF4A00] text-white rounded-br-md"
                            : "bg-white border border-slate-200 text-[#09090B] rounded-bl-md shadow-sm"
                        }`}
                        data-testid={`convo-msg-${m.message_id}`}
                      >
                        <p className="whitespace-pre-wrap">{m.content}</p>
                        <p className={`text-[9px] mt-1 ${m.sender === "admin" ? "text-white/60" : "text-[#9CA3AF]"}`}>
                          {new Date(m.created_at).toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>

                {/* Reply input */}
                <div className="px-4 py-3 bg-white border-t border-slate-200 flex gap-2">
                  <Input
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendReply()}
                    placeholder="Scrivi una risposta..."
                    className="rounded-xl border-slate-200 h-10 text-sm"
                    disabled={sendingReply}
                    data-testid="support-reply-input"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sendingReply || !replyText.trim()}
                    className="rounded-xl bg-[#FF4A00] text-white hover:bg-[#E04400] border-0 h-10 px-4"
                    data-testid="support-reply-send"
                  >
                    {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== USER DETAIL DIALOG ===== */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setEditingUser(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-[#09090B] rounded-2xl shadow-[4px_4px_0_#09090B]">
          {selectedUser && editingUser && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3" data-testid="user-detail-title">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl ${selectedUser.banned ? "bg-red-500" : "bg-emerald-500"}`}>
                    {selectedUser.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <span className="text-xl font-black">Dettaglio Cliente</span>
                    {selectedUser.banned && <Badge variant="destructive" className="ml-2">BLOCCATO</Badge>}
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* Editable user fields */}
                <div className="bg-[#FDFBF7] rounded-xl p-4 space-y-3 border border-slate-200">
                  <h3 className="font-bold text-sm text-[#09090B] flex items-center gap-2">
                    <Edit2 className="h-4 w-4 text-[#FF4A00]" /> Modifica Profilo
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { key: "name", label: "Nome", icon: Users },
                      { key: "email", label: "Email", icon: Mail },
                      { key: "phone", label: "Telefono", icon: Phone },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="text-[10px] uppercase tracking-widest text-[#6A564A] font-bold flex items-center gap-1 mb-1">
                          <f.icon className="h-3 w-3" />{f.label}
                        </label>
                        <Input
                          value={editingUser[f.key] || ""}
                          onChange={e => setEditingUser(prev => ({ ...prev, [f.key]: e.target.value }))}
                          className="rounded-xl border-slate-200 h-9 text-sm"
                          data-testid={`edit-user-${f.key}`}
                        />
                      </div>
                    ))}
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-[#6A564A] font-bold flex items-center gap-1 mb-1">
                        <Shield className="h-3 w-3" />Ruolo
                      </label>
                      <select
                        value={editingUser.role || ""}
                        onChange={e => setEditingUser(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full h-9 rounded-xl border border-slate-200 px-3 text-sm bg-white"
                        data-testid="edit-user-role"
                      >
                        <option value="">Nessuno</option>
                        <option value="customer">Cliente</option>
                        <option value="business">Attivita</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF]">
                    <Clock className="h-3 w-3" />
                    Registrato: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString("it-IT") : "N/A"} — via {selectedUser.auth_provider || "email"} — ID: {selectedUser.user_id}
                  </div>
                  <Button
                    onClick={saveUser}
                    disabled={saving}
                    className="w-full rounded-xl bg-[#FF4A00] text-white hover:bg-[#E04400] border-0 font-bold h-10"
                    data-testid="save-user-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Salva Modifiche Profilo
                  </Button>
                </div>

                {/* User bookings */}
                <div className="space-y-2">
                  <h3 className="font-bold text-sm text-[#09090B] flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#FF4A00]" /> Prenotazioni ({userBookings.length})
                  </h3>
                  {userBookings.length === 0 ? (
                    <div className="bg-slate-50 rounded-xl p-4 text-center text-sm text-[#6A564A]">
                      Nessuna prenotazione
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                      {userBookings.map(bk => <BookingCard key={bk.booking_id} bk={bk} context="user" />)}
                    </div>
                  )}
                </div>

                {/* Quick actions */}
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button
                    onClick={() => toggleBan(selectedUser.user_id, selectedUser.banned)}
                    className={`flex-1 rounded-xl font-bold h-10 ${selectedUser.banned ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500 hover:bg-red-600"} text-white border-0`}
                    data-testid="detail-ban-btn"
                  >
                    {selectedUser.banned ? <><UserCheck className="h-4 w-4 mr-2" />Sblocca</> : <><Ban className="h-4 w-4 mr-2" />Blocca</>}
                  </Button>
                  <Button
                    onClick={() => setConfirmDelete({ type: "user", id: selectedUser.user_id, name: selectedUser.name || selectedUser.email })}
                    variant="outline"
                    className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 h-10"
                    data-testid="detail-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />Elimina
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== BUSINESS DETAIL DIALOG ===== */}
      <Dialog open={!!selectedBiz} onOpenChange={() => { setSelectedBiz(null); setEditingBiz(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-[#09090B] rounded-2xl shadow-[4px_4px_0_#09090B]">
          {selectedBiz && editingBiz && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3" data-testid="biz-detail-title">
                  <div className="w-12 h-12 rounded-xl border-2 border-[#09090B] bg-[#FFE600] flex items-center justify-center font-heading text-xl text-[#09090B]">
                    {selectedBiz.name?.[0]?.toUpperCase() || "A"}
                  </div>
                  <div>
                    <span className="text-xl font-black">Dettaglio Attivita</span>
                    <Badge className="ml-2 bg-[#FF4A00] text-white border-0">{selectedBiz.category}</Badge>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 mt-4">
                {/* Editable business fields */}
                <div className="bg-[#FDFBF7] rounded-xl p-4 space-y-3 border border-slate-200">
                  <h3 className="font-bold text-sm text-[#09090B] flex items-center gap-2">
                    <Edit2 className="h-4 w-4 text-[#FF4A00]" /> Modifica Attivita
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { key: "name", label: "Nome", icon: Store },
                      { key: "category", label: "Categoria", icon: Store },
                      { key: "address", label: "Indirizzo", icon: MapPin },
                      { key: "phone", label: "Telefono", icon: Phone },
                      { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
                      { key: "deposit_amount", label: "Cauzione (€)", icon: CreditCard, type: "number" },
                      { key: "satispay_phone", label: "Satispay Tel.", icon: CreditCard },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="text-[10px] uppercase tracking-widest text-[#6A564A] font-bold flex items-center gap-1 mb-1">
                          <f.icon className="h-3 w-3" />{f.label}
                        </label>
                        <Input
                          type={f.type || "text"}
                          value={editingBiz[f.key] ?? ""}
                          onChange={e => setEditingBiz(prev => ({ ...prev, [f.key]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value }))}
                          className="rounded-xl border-slate-200 h-9 text-sm"
                          data-testid={`edit-biz-${f.key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#6A564A] font-bold flex items-center gap-1 mb-1">
                      <Edit2 className="h-3 w-3" />Descrizione
                    </label>
                    <textarea
                      value={editingBiz.description || ""}
                      onChange={e => setEditingBiz(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm resize-none"
                      data-testid="edit-biz-description"
                    />
                  </div>
                  <div className="text-[10px] text-[#9CA3AF]">
                    ID: {selectedBiz.business_id} — Owner: {selectedBiz.owner_id}
                  </div>
                  <Button
                    onClick={saveBiz}
                    disabled={saving}
                    className="w-full rounded-xl bg-[#FF4A00] text-white hover:bg-[#E04400] border-0 font-bold h-10"
                    data-testid="save-biz-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Salva Modifiche Attivita
                  </Button>
                </div>

                {/* Business bookings */}
                <div className="space-y-2">
                  <h3 className="font-bold text-sm text-[#09090B] flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#FF4A00]" /> Prenotazioni ({bizBookings.length})
                  </h3>
                  {bizBookings.length === 0 ? (
                    <div className="bg-slate-50 rounded-xl p-4 text-center text-sm text-[#6A564A]">
                      Nessuna prenotazione ricevuta
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                      {bizBookings.map(bk => <BookingCard key={bk.booking_id} bk={bk} context="biz" />)}
                    </div>
                  )}
                </div>

                {/* Quick actions */}
                <div className="flex gap-2 pt-2 border-t border-slate-100">
                  <Button
                    onClick={() => setConfirmDelete({ type: "biz", id: selectedBiz.business_id, name: selectedBiz.name })}
                    variant="outline"
                    className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 h-10 w-full"
                    data-testid="delete-biz-detail-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />Elimina Attivita
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== CONFIRM DELETE DIALOG ===== */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="max-w-sm border-2 border-red-500 rounded-2xl shadow-[4px_4px_0_#EF4444]">
          <div className="text-center space-y-4 py-2">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <Trash2 className="h-8 w-8 text-red-500" />
            </div>
            <div>
              <h3 className="font-black text-lg text-[#09090B]">Conferma eliminazione</h3>
              <p className="text-sm text-[#6A564A] mt-1">
                Stai per eliminare <strong>{confirmDelete?.name}</strong>. Questa azione e irreversibile.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setConfirmDelete(null)}
                variant="outline"
                className="flex-1 rounded-xl h-11 font-bold"
                data-testid="cancel-delete-btn"
              >
                Annulla
              </Button>
              <Button
                onClick={() => {
                  if (confirmDelete?.type === "user") deleteUser(confirmDelete.id);
                  else if (confirmDelete?.type === "biz") deleteBiz(confirmDelete.id);
                }}
                className="flex-1 rounded-xl bg-red-500 text-white hover:bg-red-600 border-0 h-11 font-bold"
                data-testid="confirm-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />Elimina
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
